import { useCallback, useRef, useState } from 'react';
import { data_from_server_post } from '@/shared/http/client';
import { normalizeTools } from './agentConfigModel';

export const DISCOVERY_STATUS = Object.freeze({
    idle: 'idle',
    loading: 'loading',
    success: 'success',
    error: 'error',
});

const looksLikeUrl = (value) => /^https?:\/\/\S+$/i.test((value || '').trim());

/**
 * Live tool discovery for MCP / Swagger sources via `POST agent-tools/discover`.
 *
 * Keeps one `{status, error, transport, url}` record per source id. A newer
 * request for the same source supersedes older in-flight ones (their result
 * is ignored), so fast URL edits cannot apply a stale tool list.
 */
export const useToolDiscovery = () => {
    const [statusById, setStatusById] = useState({});
    const requestSeq = useRef({});

    const setStatus = useCallback((sourceId, patch) => {
        setStatusById((prev) => ({ ...prev, [sourceId]: { ...(prev[sourceId] || {}), ...patch } }));
    }, []);

    const clearStatus = useCallback((sourceId) => {
        setStatusById((prev) => {
            if (!(sourceId in prev)) return prev;
            const { [sourceId]: _removed, ...rest } = prev;
            return rest;
        });
    }, []);

    /**
     * Discovers tools for `source` and resolves with `{tools, error, transport}`.
     * The caller applies `tools` to the source (or ignores stale results).
     */
    const discover = useCallback(
        async (sourceId, type, url) => {
            const trimmed = (url || '').trim();
            if (!looksLikeUrl(trimmed)) {
                const error = 'Enter a valid link starting with http:// or https://';
                setStatus(sourceId, { status: DISCOVERY_STATUS.error, error, url: trimmed });
                return { tools: [], error, stale: false };
            }
            const seq = (requestSeq.current[sourceId] || 0) + 1;
            requestSeq.current[sourceId] = seq;
            setStatus(sourceId, { status: DISCOVERY_STATUS.loading, error: null, url: trimmed });

            let response = null;
            let failure = null;
            await data_from_server_post(
                'agent-tools/discover',
                { type, url: trimmed },
                (result) => {
                    response = result;
                },
                (err) => {
                    failure = err;
                }
            );
            if (requestSeq.current[sourceId] !== seq) return { tools: [], error: null, stale: true };

            if (failure || !response) {
                const error =
                    (typeof failure === 'string' && failure) ||
                    failure?.message ||
                    'Tool discovery failed';
                setStatus(sourceId, { status: DISCOVERY_STATUS.error, error });
                return { tools: [], error, stale: false };
            }
            if (!response.success) {
                const error = response.error || 'Tool discovery failed';
                setStatus(sourceId, { status: DISCOVERY_STATUS.error, error, transport: response.transport });
                return { tools: [], error, stale: false };
            }
            const tools = normalizeTools(response.tools);
            setStatus(sourceId, {
                status: DISCOVERY_STATUS.success,
                error: null,
                transport: response.transport || null,
            });
            return { tools, error: null, transport: response.transport || null, stale: false };
        },
        [setStatus]
    );

    return { statusById, discover, clearStatus };
};

export default useToolDiscovery;
